import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from "@reduxjs/toolkit";
import { deleteMyList, deleteSCList } from "components/features/Corp/CorpUser.Slice";
import { axiosGet, axiosPatch, axiosPost, axiosPut } from "utils/APIHelper";
import { formFilterRequestPayload } from "utils/CommonFunctions";
import { responseTypes, serviceTypes } from "utils/Types";

const initialState: any = {
  filters: [],
  currentParent: "",
  modifiedFilters: {},
  doNotUpdateFilterState: false,
  filterCategories: [],
  propertyList: [],
  planInfo: [],
  isFilterApiCalled: false,
  filterRequestPayload: {},
  searchPlanList: [],
  searchManagements: [],
  searchOwnersGroup: [],
  searchOwners: [],
  ownerInfo: [],
  savedFilter: {},
  defaultFilterModified: {},
  isStatus: false,
  isOpenCorpWizard: true,
  view: "",
  showLoader: false
};

export const fetchFilterPropertyInfo = createAsyncThunk(`filter/PropertyInfo`, async (data: any, thunkApi) => {
  const { rejectWithValue } = thunkApi;
  const API_URL = `property-info?inncodes=${data?.innCodesValue}`;
  const response: any = await axiosGet(API_URL);
  if (response.status !== responseTypes.success) {
    return rejectWithValue(response.response.data);
  }
  return response.data;
});

export const fetchManagementcOInfo = createAsyncThunk(`filter/ManagementInfo`, async (data: any, thunkApi) => {
  const { rejectWithValue } = thunkApi;
  const API_URL = `management-company?searchText=${data}`;
  const response: any = await axiosGet(API_URL);
  if (response.status !== responseTypes.success) {
    return rejectWithValue(response.response.data);
  }
  return response.data;
});

export const fetchOwnerInfo = createAsyncThunk(`filter/OwnerInfo`, async (data: any, thunkApi) => {
  const { rejectWithValue } = thunkApi;
  const API_URL = `owner-groups?searchText=${data}`;
  const response: any = await axiosGet(API_URL);
  if (response.status !== responseTypes.success) {
    return rejectWithValue(response.response.data);
  }
  return response.data;
});

export const fetchFilterSelectedPlansAsync = createAsyncThunk("filters/plansDetails", async (data: any, thunkApi) => {
  const API_URL = `plans?externalHltId=${data?.externalHltId || ""}`;
  const response: any = await axiosPost(API_URL, "", {
    ...data?.payload
  });

  if (
    response?.response?.status >= responseTypes.internal_error &&
    response?.response?.status < responseTypes.other_error
  ) {
    return thunkApi.rejectWithValue(response.response);
  }

  return response.data;
});

export const fetchCorpFilterCategories = createAsyncThunk(
  "filters/fetchCorpFilterCategories",
  async (thunkApi: any) => {
    const API_URL = "corp/filter-categories";
    const response: any = await axiosGet(API_URL, serviceTypes.userplan);
    if (response.response?.status === responseTypes.not_found) {
      return thunkApi.rejectWithValue(response.response.data);
    }
    return response.data;
  }
);

export const fetchCorpSavedFilters = createAsyncThunk("filters/fetchCorpSavedFilters", async (data: any, thunkApi) => {
  const API_URL = `corp/filters?types=${data?.types}`;
  const response: any = await axiosGet(API_URL, serviceTypes.userplan);
  if (response.response?.status === responseTypes.not_found) {
    return thunkApi.rejectWithValue(response.response.data);
  }
  return { res: response.data, isClose: data.isModalClose };
});

export const saveFilterByType = createAsyncThunk("filters/postSavedFilters", async (data: any, thunkApi) => {
  const API_URL = `corp/filters`;
  const response: any = await axiosPost(API_URL, serviceTypes.userplan, data);
  if (response.response?.status === responseTypes.error) {
    return thunkApi.rejectWithValue(response.response);
  }
  return response;
});

export const createFilterByType = createAsyncThunk("filters/createListFilters", async (data: any, thunkApi) => {
  const API_URL = `corp/filters`;
  const response: any = await axiosPost(API_URL, serviceTypes.userplan, data);
  if (response.response?.status === responseTypes.error) {
    return thunkApi.rejectWithValue(response.response);
  }
  return response;
});

export const updateCorpSavedFilters = createAsyncThunk(
  "filters/updateCorpSavedFilters",
  async (data: any, thunkApi) => {
    const API_URL = `corp/filters`;
    const response: any = await axiosPut(API_URL, serviceTypes.userplan, data);
    if (response?.response?.status >= responseTypes.error) {
      return thunkApi.rejectWithValue(response.response);
    }

    return response;
  }
);

export const updateStaticList = createAsyncThunk("filters/updateStaticList", async (data: any, thunkApi) => {
  const API_URL = `corp/filters`;
  const response: any = await axiosPut(API_URL, serviceTypes.userplan, data);
  if (response?.response?.status >= responseTypes.error) {
    return thunkApi.rejectWithValue(response.response);
  }

  return response;
});

export const duplicateFilter = createAsyncThunk("filters/duplicate-filters", async (data: any, thunkApi) => {
  const API_URL = `corp/duplicate-filters`;
  const response: any = await axiosPost(API_URL, serviceTypes.userplan, data);
  if (response.status === responseTypes.success) {
    return thunkApi.fulfillWithValue(response.data);
  } else {
    return thunkApi.rejectWithValue(response.response.data);
  }
});
export const updateCorpSavedFiltersConsultant = createAsyncThunk("filters/conultant", async (data: any, thunkApi) => {
  const API_URL = `inncode-groups/${data.listId}`;
  const response: any = await axiosPatch(API_URL, serviceTypes.userplan, data.payload);

  if (response?.response?.status >= responseTypes.error) {
    return thunkApi.rejectWithValue(response.response);
  }

  return response;
});

export const FiltersSlice = createSlice({
  name: "corp filters",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = [...(action?.payload || [])];
      state.filterRequestPayload = formFilterRequestPayload(action?.payload);
    },
    clearFilters: (state, action) => {
      if (!action?.payload?.skipView) state.view = "";
      if (action?.payload?.doNotUpdateFilterState) state.doNotUpdateFilterState = true;
      if (action?.payload?.eliminateMyList) {
        delete state.modifiedFilters["listDisplay"];
        delete state.modifiedFilters["consultantListDisplay"];
        delete state.defaultFilterModified["listDisplay"];
        delete state.defaultFilterModified["consultantListDisplay"];
        state.modifiedFilters = { ...state.modifiedFilters };
        state.defaultFilterModified = { ...state.defaultFilterModified };
      }
      if (!action?.payload?.skipModified && !action?.payload?.eliminateMyList) {
        state.modifiedFilters = {};
        state.defaultFilterModified = {};
      }
      state.filters = [];

      state.filterRequestPayload = {};
    },
    setCheckedDynamicInncode: (state, action) => {
      state.propertyList = [
        ...state.propertyList,
        { innCode: action?.payload?.innCode, propName: action?.payload?.propName }
      ];
    },
    setCheckedPlanInfo: (state, action) => {
      state.planInfo = [...state.planInfo, { id: action?.payload?.id, name: action?.payload?.name }];
    },
    setCheckedOwnerInfo: (state, action) => {
      state.ownerInfo = [...state.ownerInfo, { id: action?.payload?.id, name: action?.payload?.name }];
    },
    setDefaultFilterModified: (state, action) => {
      if (
        action?.payload?.isFilterChanged &&
        action?.payload?.isApplied &&
        ["dashboardDisplay", "listDisplay", "consultantListDisplay"].includes(state.currentParent)
      )
        state.modifiedFilters[state.currentParent] = state.filters;
      else if (!action?.payload?.isFilterChanged && !action?.payload?.isApplied) {
        delete state.modifiedFilters[state.currentParent];
        state.modifiedFilters = { ...state.modifiedFilters };
      }
      state.defaultFilterModified[state.currentParent] = { ...action?.payload };
    },
    setDefaultAPiCalled: (state) => {
      state.isStatus = true;
    },
    setCurrentParent: (state, action) => {
      state.currentParent = action?.payload?.parentPage;
    },
    setSavedFilter: (state, action) => {
      const res = [...state.savedFilter[action?.payload?.name], action?.payload?.data];
      state.savedFilter = { ...state.savedFilter, [action.payload.name]: res };
    },
    setSavedFilterByType: (state, action) => {
      state.savedFilter[action.payload.type] = action.payload.data;
    },
    searchOwners: (state, action) => {
      state.searchOwners = action?.payload?.userList
        ?.filter(
          (item: any) =>
            item?.email?.toLowerCase()?.includes(action?.payload?.searchText?.toLowerCase()) ||
            item?.displayName?.toLowerCase()?.includes(action?.payload?.searchText?.toLowerCase())
        )
        ?.map((item2: any) => {
          return { id: item2?.hltId, name: item2?.displayName };
        });
    },
    setView: (state, action) => {
      state.view = action?.payload;
    },
    setLoader: (state, action) => {
      state.showLoader = action.payload;
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchFilterPropertyInfo.fulfilled, (state, action: any) => {
        state.propertyList = [...state.propertyList, ...action.payload];
      })
      .addCase(fetchFilterSelectedPlansAsync.fulfilled, (state, action: any) => {
        if (action?.meta.arg.isSearched) state.searchPlanList = action?.payload?.plans;
        else state.planInfo = [...state.planInfo, ...action.payload.plans];
      })
      .addCase(fetchManagementcOInfo.fulfilled, (state, action: any) => {
        state.searchManagements = action?.payload;
      })
      .addCase(fetchOwnerInfo.fulfilled, (state, action: any) => {
        state.searchOwnersGroup = action?.payload;
      })
      .addCase(fetchCorpFilterCategories.fulfilled, (state, action: any) => {
        state.filterCategories = action.payload?.filter;
      })
      .addCase(fetchCorpSavedFilters.pending, (state, action: any) => {
        state.showLoader = true;
      })
      .addCase(fetchCorpSavedFilters.fulfilled, (state, action: any) => {
        state.savedFilter = action.payload?.res;
        state.isFilterApiCalled = true;
        if (action?.payload?.isClose) {
          state.isOpenCorpWizard = false;
        }
        state.showLoader = false;
      })
      .addCase(fetchCorpSavedFilters.rejected, (state, action: any) => {
        state.showLoader = false;
      })
      .addCase(saveFilterByType.pending, (state, action: any) => {
        state.showLoader = true;
      })
      .addCase(updateStaticList.pending, (state, action: any) => {
        state.showLoader = true;
      })
      .addCase(updateStaticList.fulfilled, (state, action: any) => {
        state.showLoader = false;
      })
      .addCase(updateStaticList.rejected, (state, action: any) => {
        state.showLoader = false;
      })

      .addCase(saveFilterByType.fulfilled, (state, action: any) => {
        state.showLoader = false;
        state.savedFilter = { ...state.savedFilter, ...action.payload?.data };
      })
      .addCase(saveFilterByType.rejected, (state, action: any) => {
        state.showLoader = false;
      })
      .addCase(createFilterByType.pending, (state, action: any) => {
        state.showLoader = true;
      })
      .addCase(createFilterByType.fulfilled, (state, action: any) => {
        state.showLoader = false;
      })
      .addCase(createFilterByType.rejected, (state, action: any) => {
        state.showLoader = false;
      })
      .addCase(updateCorpSavedFiltersConsultant.fulfilled, (state, action: any) => {
        state.showLoader = false;
        state.savedFilter = { ...state.savedFilter, ...action.payload?.data };
      })
      .addMatcher(
        isFulfilled(deleteMyList, deleteSCList, duplicateFilter, updateCorpSavedFilters),
        (state, action: any) => {
          const payload =
            action.type === "filters/updateCorpSavedFilters/fulfilled" ? action.payload?.data : action.payload;
          if ("static" in payload) {
            state.savedFilter.static = payload.static;
          }
          if ("dynamic" in payload) {
            state.savedFilter.dynamic = payload.dynamic;
          }
          if ("consultant" in payload) {
            state.savedFilter.consultant = payload.consultant;
          }
          if (action.meta?.arg?.resetFilter) {
            delete state.modifiedFilters["listDisplay"];
            delete state.modifiedFilters["consultantListDisplay"];
            delete state.defaultFilterModified["listDisplay"];
            delete state.defaultFilterModified["consultantListDisplay"];
            state.modifiedFilters = { ...state.modifiedFilters };
            state.defaultFilterModified = { ...state.defaultFilterModified };
            state.filters = [];
            state.filterRequestPayload = {};
          }
          state.showLoader = false;
        }
      )

      .addMatcher(isRejected(deleteMyList, deleteSCList, duplicateFilter, updateCorpSavedFilters), (state) => {
        state.showLoader = false;
      })
      .addMatcher(isPending(deleteMyList, deleteSCList, duplicateFilter, updateCorpSavedFilters), (state) => {
        state.showLoader = true;
      });
  }
});

export const {
  setFilters,
  clearFilters,
  setCheckedDynamicInncode,
  setCheckedPlanInfo,
  setDefaultFilterModified,
  setDefaultAPiCalled,
  setView,
  searchOwners,
  setCheckedOwnerInfo,
  setCurrentParent,
  setSavedFilter,
  setSavedFilterByType,
  setLoader
} = FiltersSlice.actions;

export default FiltersSlice.reducer;
